import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { TrendingUp, ArrowRight } from 'lucide-react';
import { startOfDay, startOfWeek, startOfMonth } from 'date-fns';
import { supabase } from '../supabaseClient';
import { orderService } from '../services/orderService';
import Card from './shared/Card';
import { Skeleton } from './shared/Skeleton';

const EMPTY_PERIOD = { orders: 0, lines: 0 };

/**
 * Company-wide portal order counts for today / this week / this month, plus the
 * signed-in user's own open orders. Neutral on purpose: these are volumes, not
 * statuses, so they carry no colour. Bump refreshKey to refetch.
 */
const OrderStatsWidget = ({ refreshKey = 0 }) => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [periods, setPeriods] = useState({ daily: EMPTY_PERIOD, weekly: EMPTY_PERIOD, monthly: EMPTY_PERIOD });
  const [outstanding, setOutstanding] = useState({ total_outstanding: 0, total_outstanding_value: 0 });

  const fetchOrderStats = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const [outstandingSummary, { data: account }] = await Promise.all([
        orderService.getOutstandingOrdersSummary(),
        supabase.from('accounts').select('company_name').eq('id', user.id).single(),
      ]);

      const { data: companyUsers } = await supabase
        .from('accounts')
        .select('id')
        .eq('company_name', account?.company_name || '');
      const userIds = companyUsers?.length ? companyUsers.map((u) => u.id) : [user.id];

      const now = new Date();
      const todayStart = startOfDay(now);
      const weekStart = startOfWeek(now, { weekStartsOn: 1 });
      const monthStart = startOfMonth(now);
      // A week can start in the previous month, so fetch from whichever is earlier.
      const fetchFrom = weekStart < monthStart ? weekStart : monthStart;

      const { data: orders, error } = await supabase
        .from('orders')
        .select('created_at, cart_items')
        .in('user_id', userIds)
        .gte('created_at', fetchFrom.toISOString());
      if (error) throw error;

      const summarise = (rangeStart) => {
        const inRange = (orders || []).filter((o) => new Date(o.created_at) >= rangeStart);
        return {
          orders: inRange.length,
          lines: inRange.reduce((total, o) => total + (o.cart_items?.length || 0), 0),
        };
      };

      setPeriods({
        daily: summarise(todayStart),
        weekly: summarise(weekStart),
        monthly: summarise(monthStart),
      });
      setOutstanding(outstandingSummary);
    } catch (error) {
      console.error('Error fetching order stats:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrderStats();
    const interval = setInterval(fetchOrderStats, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchOrderStats, refreshKey]);

  const columns = [
    { label: 'Today', ...periods.daily },
    { label: 'This week', ...periods.weekly },
    { label: 'This month', ...periods.monthly },
  ];

  return (
    <Card
      padding="none"
      header={
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-gray-500" aria-hidden="true" />
            Company Orders
          </h2>
          <span className="text-xs text-gray-500">All portal users at your company</span>
        </div>
      }
    >
      <dl className="grid grid-cols-3 divide-x divide-gray-200">
        {columns.map((col) => (
          <div key={col.label} className="px-4 sm:px-6 py-4">
            <dt className="text-sm font-medium text-gray-600">{col.label}</dt>
            {loading ? (
              <Skeleton className="h-8 w-12 mt-1" />
            ) : (
              <dd className="text-2xl font-bold text-gray-900 mt-1 tabular-nums">{col.orders}</dd>
            )}
            <p className="text-xs text-gray-500 mt-0.5">
              {col.orders === 1 ? 'order' : 'orders'}
              {!loading && ` · ${col.lines} ${col.lines === 1 ? 'line' : 'lines'}`}
            </p>
          </div>
        ))}
      </dl>

      {!loading && outstanding.total_outstanding > 0 && (
        <button
          onClick={() => history.push('/user/recent-orders')}
          className="w-full flex items-center justify-between gap-3 px-4 sm:px-6 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg text-sm text-gray-700 hover:bg-gray-100 transition-colors text-left group focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
        >
          <span>
            You have{' '}
            <span className="font-semibold text-gray-900 tabular-nums">{outstanding.total_outstanding}</span>{' '}
            open {outstanding.total_outstanding === 1 ? 'order' : 'orders'} worth{' '}
            <span className="font-semibold text-gray-900 tabular-nums">
              {orderService.formatCurrency(outstanding.total_outstanding_value)}
            </span>
          </span>
          <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors flex-shrink-0" aria-hidden="true" />
        </button>
      )}
    </Card>
  );
};

export default OrderStatsWidget;
